import * as React from 'react'
import * as R from 'ramda'
import RaisedButton from 'material-ui/RaisedButton'
import TextField from 'material-ui/TextField'
import SelectField from 'material-ui/SelectField'
import MenuItem from 'material-ui/MenuItem'
import Table from 'material-ui/Table/Table'
import TableHeader from 'material-ui/Table/TableHeader'
import TableRow from 'material-ui/Table/TableRow'
import TableHeaderColumn from 'material-ui/Table/TableHeaderColumn'
import TableBody from 'material-ui/Table/TableBody'
import TableRowColumn from 'material-ui/Table/TableRowColumn'
import FlatButton from 'material-ui/FlatButton'
import ImagePictureAsPdf from 'material-ui/svg-icons/image/picture-as-pdf'
import DeleteForever from 'material-ui/svg-icons/action/delete'
import {ParcelsTab, ProductShippingServiceSection} from './ParcelsState'
import {ParcelsSubAction} from './ParcelsActions'
import {ParcelWithDetails} from '../../../Entities/ParcelWithDetails'
import {ParcelPositionWithPosition} from '../../../Entities/ParcelPositionWithPosition'
import {WebData} from '../../../Data/WebData'
import {ParcelDispatcher} from '../../../Entities/ParcelDispatcher'
import IconButton from 'material-ui/IconButton'
import injectSheet from 'react-jss'
import {card, tabs} from '../../../Style'
import {Card} from 'material-ui-next'

const styles = {
    ...card,
    ...tabs
}

interface Props {
    readonly parcelDispatchers: WebData<ParcelDispatcher[]>
    readonly state: ParcelsTab
    readonly dispatch: (action: ParcelsSubAction) => void

}

export const ParcelsTabView =
    injectSheet<Props>(styles)(
        ({classes, state, dispatch, parcelDispatchers}) => (
            <div>
                {R.map(
                    section =>
                        <SectionElement
                            key={section.productShippingService.id}
                            parcelDispatchers={parcelDispatchers}
                            section={section}
                            dispatch={dispatch}
                        />,
                    state.sections)}
            </div>
        )
    )

interface PropsSectionElement {
    readonly parcelDispatchers: WebData<ParcelDispatcher[]>
    readonly section: ProductShippingServiceSection
    readonly dispatch: (action: ParcelsSubAction) => void
}

export const SectionElement =
    injectSheet<PropsSectionElement>(styles)(
        ({classes, dispatch, parcelDispatchers, section}) => (
            <div className={classes.tabContent}>
                <Card className={classes.card}>
                    <div className={classes.cardRow}>
                        <div className={classes.cardColumn}>
                            <div className={classes.cardHeader}>
                                {section.productShippingService.longName}</div>
                            <div className={classes.cardContent}>

                                {section.openPositionRows.length > 0
                                    ? <CreateParcelForm
                                        parcelDispatchers={parcelDispatchers}
                                        section={section}
                                        dispatch={dispatch}
                                    />
                                    : <p>Alle Positionen wurden zugewiesen</p>
                                }
                            </div>
                        </div>
                    </div>
                </Card>
                <Card className={classes.card}>
                    <div className={classes.cardColumn}>
                        <div className={classes.cardHeader}>
                            Paket/e
                        </div>
                        <div className={classes.cardContent}>
                            {R.map(
                                parcel =>
                                    <PackageElement
                                        key={parcel.parcel.id}
                                        parcel={parcel}
                                        dispatch={dispatch}
                                        productShippingServiceId={section.productShippingService.id}
                                    />,
                                section.parcels)}
                        </div>
                    </div>
                </Card>
            </div>
        )
    )

interface PropsCreateParcelForm {
    parcelDispatchers: WebData<ParcelDispatcher[]>
    section: ProductShippingServiceSection
    dispatch: (action: ParcelsSubAction) => void
}

export const CreateParcelForm =
    injectSheet<PropsCreateParcelForm>(styles)(
        ({classes, dispatch, parcelDispatchers, section}) => (
            <form
                noValidate={true}
                onSubmit={event => {
                    event.preventDefault()
                    dispatch({
                        type: 'SubmitParcel',
                        productShippingServiceId: section.productShippingService.id,
                    })
                }}
            >
                <div className="package-options">
                    <TextField
                        floatingLabelText="Trackingnummer"
                        style={{
                            marginRight: '10px',
                            width: 'auto',
                            flex: '1'
                        }}
                        value={section.input.trackingNumber}
                        onChange={(event, value: string) => dispatch({
                            type: 'TrackingNumberChanged',
                            productShippingServiceId: section.productShippingService.id,
                            value,
                        })}
                        errorText={
                            (section.validation.trackingNumber === 'Mandatory') ?
                                'Bitte eine Trackingnummer eingeben!' : null}
                        id={`tracking-number-input-${section.productShippingService.id}`}
                    />
                    <ParcelDispatchersView
                        parcelDispatchers={parcelDispatchers}
                        section={section}
                        dispatch={dispatch}
                    />
                </div>
                <OpenPositionsTable
                    section={section}
                    dispatch={dispatch}
                />
                {section.validation.positions === 'EmptyArray' &&
                <div>
                    Sie müssen mindestens eine offene Position auswählen!
                </div>
                }
                <RaisedButton
                    label="Paket schnüren"
                    primary={true}
                    type="submit"
                    style={{margin: '8px', float: 'right'}}
                />
            </form>
        )
    )

function OpenPositionsTable({section, dispatch}: {
    section: ProductShippingServiceSection,
    dispatch: (action: ParcelsSubAction) => void,
}) {

    return (
        <Table
            selectable={true}
            fixedHeader={false}
            style={{marginTop: '16px'}}
            multiSelectable={true}
            onRowSelection={
                (selected: number[] | 'all') => dispatch({
                    type: 'OpenPositionsSelectionChanged',
                    productShippingServiceId: section.productShippingService.id,
                    selected,
                })
            }
        >
            <TableHeader>
                <TableRow>
                    <TableHeaderColumn>Name</TableHeaderColumn>
                    <TableHeaderColumn>Anzahl</TableHeaderColumn>
                    <TableHeaderColumn>Verfügbar</TableHeaderColumn>
                    <TableHeaderColumn style={{textAlign: 'right'}}>
                        Gewicht
                    </TableHeaderColumn>
                    <TableHeaderColumn style={{textAlign: 'right'}}>
                        Preis (netto)
                    </TableHeaderColumn>
                    <TableHeaderColumn style={{textAlign: 'center'}}>
                        PDF
                    </TableHeaderColumn>
                </TableRow>
            </TableHeader>
            <TableBody
                deselectOnClickaway={false}
            >
                {R.map(
                    openPositionRow =>
                        <TableRow
                            selected={openPositionRow.input.selected}
                            key={openPositionRow.position.id}
                        >
                            <TableRowColumn>{openPositionRow.position.name}</TableRowColumn>
                            <TableRowColumn>
                                <div
                                    onClick={event => {
                                        event.stopPropagation()
                                    }}
                                >
                                    <TextField
                                        disabled={!openPositionRow.input.selected}
                                        value={openPositionRow.input.amount}
                                        onChange={(event,
                                                   value: string) => dispatch({
                                            type: 'OpenPositionAmountChanged',
                                            productShippingServiceId: section.productShippingService.id,
                                            id: openPositionRow.position.id,
                                            value,
                                        })}
                                        errorText={
                                            openPositionRow.validation.amount === 'Mandatory' ?
                                                'Bitte geben Sie die verpackte Menge an' :
                                                openPositionRow.validation.amount === 'InvalidInt' ?
                                                    'Ungültige Mengenangabe' :
                                                    openPositionRow.validation.amount === 'TooSmall' ?
                                                        'Ungültige Mengenangabe (zu wenig)' :
                                                        openPositionRow.validation.amount === 'TooBig' ?
                                                            'Ungültige Mengenangabe (zu viel)' :
                                                            null}
                                        type="number"
                                        min={1}
                                        max={openPositionRow.remainingAmount}
                                        step={1}
                                    />
                                </div>
                            </TableRowColumn>
                            <TableRowColumn>{openPositionRow.remainingAmount}</TableRowColumn>
                            <TableRowColumn style={{textAlign: 'right'}}>
                                {openPositionRow.position.weight.toFixed(2)}
                                gr
                            </TableRowColumn>
                            <TableRowColumn style={{textAlign: 'right'}}>
                                {openPositionRow.position.totalNetPrice !== null ?
                                    openPositionRow.position.totalNetPrice.toFixed(2) :
                                    0}
                                €
                            </TableRowColumn>
                            <TableRowColumn style={{textAlign: 'center'}}>
                                <FlatButton
                                    icon={<ImagePictureAsPdf/>}
                                    className="btn-icon"
                                />
                            </TableRowColumn>
                        </TableRow>,
                    section.openPositionRows)}
            </TableBody>
        </Table>
    )
}

function ParcelDispatchersView({parcelDispatchers, section, dispatch}: {
    parcelDispatchers: WebData<ParcelDispatcher[]>
    section: ProductShippingServiceSection,
    dispatch: (action: ParcelsSubAction) => void,
}) {
    switch (parcelDispatchers.type) {
        case 'Loading':
            return (
                <SelectField
                    style={{width: 'auto', flex: '1'}}
                    floatingLabelText="Lade Dienstleister..."
                    disabled={true}
                />)
        case 'Success':
            return (
                <SelectField
                    style={{width: 'auto', flex: '1'}}
                    floatingLabelText="Dienstleister"
                    value={section.input.parcelDispatcherId}
                    onChange={(event, index, value: number) => dispatch({
                        type: 'ParcelDispatcherIdChanged',
                        productShippingServiceId: section.productShippingService.id,
                        value,
                    })}
                    errorText={
                        section.validation.parcelDispatcherId === 'Mandatory'
                            ? 'Bitte einen Versanddienstleister auswählen!'
                            : null
                    }
                >
                    {R.map(
                        parcelDispatcher =>
                            <MenuItem
                                primaryText={parcelDispatcher.name}
                                value={parcelDispatcher.id}
                                key={parcelDispatcher.id}
                            />,
                        parcelDispatchers.value)}
                </SelectField>)
        case 'Failure':
            return (
                <SelectField
                    style={{width: 'auto', flex: '1'}}
                    floatingLabelText="Fehler beim Laden der Dienstleister"
                    disabled={true}
                />)
    }
}

function PackageElement({productShippingServiceId, parcel, dispatch}: {
    productShippingServiceId: number,
    parcel: ParcelWithDetails,
    dispatch: (action: ParcelsSubAction) => void
}) {
    return (
        <div className="package">
            <div style={{float: 'right', margin: '16px'}}>
                <IconButton
                    onTouchTap={() => {
                        dispatch({
                            type: 'DeleteParcel',
                            parcelId: parcel.parcel.id,
                            productShippingServiceId: productShippingServiceId
                        })
                    }}
                >
                    <DeleteForever/>
                </IconButton>
            </div>
            <p>
                <b>Paketnummer:</b> {parcel.parcel.id} <br/>
                <b>versandt
                    am:</b> {parcel.parcel.created.toLocalDate().toString()}
            </p>
            <p>
                <b>Trackingnummer:</b>
                <span> {parcel.parcel.trackingNumber}</span>
            </p>
            <Table
                selectable={false}
                fixedHeader={false}
                style={{marginTop: '16px'}}
            >
                <TableHeader
                    displaySelectAll={false}
                    adjustForCheckbox={false}
                >
                    <TableRow>
                        <TableHeaderColumn>Name</TableHeaderColumn>
                        <TableHeaderColumn>Anzahl</TableHeaderColumn>
                        <TableHeaderColumn style={{textAlign: 'right'}}>
                            Gewicht
                        </TableHeaderColumn>
                        <TableHeaderColumn style={{textAlign: 'right'}}>
                            Preis (netto)
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            style={{textAlign: 'center'}}
                        >
                            PDF
                        </TableHeaderColumn>
                    </TableRow>
                </TableHeader>
                <TableBody displayRowCheckbox={false}>
                    {R.map(
                        parcelPosition =>
                            <ParcelPositionRowElement
                                key={parcelPosition.position.id}
                                parcelPosition={parcelPosition}
                            />,
                        parcel.parcelPositionsWithPosition)}
                    <TableRow>
                        <TableRowColumn
                            colSpan={5}
                        >
                            <b>VDP -Produkt-ID: </b>
                            07-00-01-01-0006-00-26586
                        </TableRowColumn>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    )
}

function ParcelPositionRowElement({parcelPosition}: {
    parcelPosition: ParcelPositionWithPosition,
}) {
    return (
        <TableRow>
            <TableRowColumn>{parcelPosition.position.name}</TableRowColumn>
            <TableRowColumn>{parcelPosition.parcelPosition.amount}</TableRowColumn>
            <TableRowColumn style={{textAlign: 'right'}}>
                {parcelPosition.position.weight.toFixed(2)} gr
            </TableRowColumn>
            <TableRowColumn style={{textAlign: 'right'}}>
                {parcelPosition.position.totalNetPrice !== null
                    ? parcelPosition.position.totalNetPrice.toFixed(2) : 0} €
            </TableRowColumn>
            <TableRowColumn style={{textAlign: 'center'}}>
                <FlatButton
                    icon={<ImagePictureAsPdf/>}
                    className="btn-icon"
                />
            </TableRowColumn>
        </TableRow>
    )
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderDetails/Parcels/ParcelsView.tsx